import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import { RootQueryType } from '@/gql/graphql';
import { useTranslations } from 'use-intl';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { TextBadge } from '@/UIlibrary/TextBadge/TextBadge';
import IconButton from '@/UIlibrary/IconButton/IconButton';
import { Tooltip } from '@/UIlibrary/Tooltip/Tooltip';
import {
	RegistryPersonalInformationKeys,
	RegistryBirthInformationKeys,
	RegistryEconomicInformationKeys,
	RegistryTaxInformationKeys,
	RegistryOfficialAddressKeys
} from '@/types/Onboarding/Registration';
import { MexStates, CountryCodesES, Nationalities, CountryCodes, Occupations } from '@/utils/maps';
import { ChevronUp, ChevronDown, ChevronsDown, ChevronsUp, ArrowLeft } from 'react-feather';
import MediaCard from './MediaCard';

export const GET_CLIENT_PROSPECTUS = gql`
	query GetClientProspect($id: ID!) {
		getRegistry(id: $id) {
			economicInformation {
				occupation
				houseType
				monthlyFamilyExpense {
					currency
					amount
				}
				monthlyFamilyIncome {
					currency
					amount
				}
				extraMonthlyIncome {
					currency
					amount
				}
			}
			birthInformation {
				dateOfBirth
				stateOfBirth
				countryOfBirth
				nationality
			}
			personalInformation {
				name
				firstLastName
				secondLastName
				hasChildren
				maritalStatus
				curp
			}
			status
			taxInformation {
				rfc
				clabe
				eSignatureSerialNumber
				taxRegimeCode
				officialIdType
			}
			officialAddress {
				city
				colony
				externalNumber
				internalNumber
				municipalityOrDelegation
				postalCode
				state
				street
			}
		}
	}
`;

const ClientProspectus: React.FC = () => {
	const { clientId, lang } = useParams();
	const { loading, data } = useQuery<RootQueryType>(GET_CLIENT_PROSPECTUS, {
		variables: { id: clientId },
		errorPolicy: 'all'
	});

	const [informationSections, setInformationSections] = useState<Record<string, any>[]>([]);
	const [openSections, setOpenSections] = useState(new Set());

	const t = useTranslations('ClientProspectus');
	const CountriesLocale = lang === 'es' ? CountryCodesES : CountryCodes;

	const sectionTitles = [
		t('PersonalInformation'),
		t('BirthInformation'),
		t('TaxInformation'),
		t('OfficialAddress'),
		t('EconomicInformation'),
		t('ContactInformation')
	];
	useEffect(() => {
		if (data?.getRegistry) {
			const sections: Record<string, any>[] = [];
			let formattedSection: Record<string, any> = {};
			Object.keys(RegistryPersonalInformationKeys).forEach((key) => {
				if (data?.getRegistry?.personalInformation) {
					const value =
						data?.getRegistry?.personalInformation[
							key as keyof typeof RegistryPersonalInformationKeys
						];
					if (typeof value === 'boolean') {
						formattedSection[key.charAt(0).toUpperCase() + key.slice(1)] = value
							? t('Yes')
							: t('No');
						return;
					}
					formattedSection[key.charAt(0).toUpperCase() + key.slice(1)] = value || '';
				} else {
					formattedSection[key.charAt(0).toUpperCase() + key.slice(1)] = '';
				}
			});
			sections.push(formattedSection);
			formattedSection = {};
			Object.keys(RegistryBirthInformationKeys).forEach((key) => {
				if (data?.getRegistry?.birthInformation) {
					const value =
						data?.getRegistry?.birthInformation[key as keyof typeof RegistryBirthInformationKeys];
					if (key === 'stateOfBirth') {
						formattedSection[key.charAt(0).toUpperCase() + key.slice(1)] =
							MexStates[value as keyof typeof MexStates];
						return;
					}
					if (key === 'nationality') {
						formattedSection[key.charAt(0).toUpperCase() + key.slice(1)] =
							Nationalities[value as keyof typeof Nationalities];
						return;
					}
					if (key === 'countryOfBirth') {
						formattedSection[key.charAt(0).toUpperCase() + key.slice(1)] =
							CountriesLocale[value as keyof typeof CountryCodes];
						return;
					}
					formattedSection[key.charAt(0).toUpperCase() + key.slice(1)] = value;
				} else {
					formattedSection[key.charAt(0).toUpperCase() + key.slice(1)] = '';
				}
			});
			sections.push(formattedSection);
			formattedSection = {};
			Object.keys(RegistryTaxInformationKeys).forEach((key) => {
				if (data?.getRegistry?.taxInformation) {
					const value =
						data?.getRegistry?.taxInformation[key as keyof typeof RegistryTaxInformationKeys];
					formattedSection[key.charAt(0).toUpperCase() + key.slice(1)] = value;
				} else {
					formattedSection[key.charAt(0).toUpperCase() + key.slice(1)] = '';
				}
			});
			sections.push(formattedSection);
			formattedSection = {};
			Object.keys(RegistryOfficialAddressKeys).forEach((key) => {
				if (data?.getRegistry?.officialAddress) {
					const value =
						data?.getRegistry?.officialAddress[key as keyof typeof RegistryOfficialAddressKeys];
					if (key === 'state') {
						formattedSection[key.charAt(0).toUpperCase() + key.slice(1)] =
							MexStates[value as keyof typeof MexStates];
						return;
					}
					formattedSection[key.charAt(0).toUpperCase() + key.slice(1)] = value;
				} else {
					formattedSection[key.charAt(0).toUpperCase() + key.slice(1)] = '';
				}
			});
			sections.push(formattedSection);
			formattedSection = {};
			Object.keys(RegistryEconomicInformationKeys).forEach((key) => {
				if (data?.getRegistry?.economicInformation) {
					const value =
						data?.getRegistry?.economicInformation[
							key as keyof typeof RegistryEconomicInformationKeys
						];
					if (typeof value === 'object') {
						formattedSection[key.charAt(0).toUpperCase() + key.slice(1)] =
							value['currency'] + ' ' + value['amount'];
						return;
					}
					if (key === 'occupation') {
						formattedSection[key.charAt(0).toUpperCase() + key.slice(1)] = t(
							Occupations[value as keyof typeof Occupations]
						);
						return;
					}
					formattedSection[key.charAt(0).toUpperCase() + key.slice(1)] = value;
				} else {
					formattedSection[key.charAt(0).toUpperCase() + key.slice(1)] = '';
				}
			});
			sections.push(formattedSection);
			formattedSection = {};
			setInformationSections(sections);
		}
	}, [data]);

	const expandAll = () => {
		setOpenSections(new Set(sectionTitles));
	};

	const collapseAll = () => {
		setOpenSections(new Set());
	};

	const TextSection: React.FC<{ title: string; value: string }> = ({ title, value }) => (
		<div className="flex flex-row gap-1 items-center justify-between">
			<Typography>{title}:</Typography>
			{value ? (
				<Typography weight="semibold">{value}</Typography>
			) : (
				<TextBadge text={t('Pending')} size="sm" />
			)}
		</div>
	);

	const InfoSectionDropdown: React.FC<{
		title: string;
		data: Record<string, any>;
		t: (key: string) => string;
		openSections: any;
		setOpenSections: React.Dispatch<React.SetStateAction<any>>;
	}> = ({ title, data, t }) => {
		const toggleSection = (title: string) => {
			setOpenSections((prev) => {
				const newSet = new Set(prev);
				if (newSet.has(title)) {
					newSet.delete(title);
				} else {
					newSet.add(title);
				}
				return newSet;
			});
		};

		if (!data) return null;

		return (
			<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
				<div className="flex flex-row items-center" onClick={() => toggleSection(title)}>
					<div className="grow cursor-pointer">
						<Typography weight="semibold">{title}</Typography>
					</div>
					{openSections.has(title) ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
				</div>
				{openSections.has(title) && (
					<div className="flex flex-col mt-3 gap-1">
						{Object.entries(data).map(([key, value]) => (
							<TextSection key={key} title={t(key)} value={value} />
						))}
					</div>
				)}
			</div>
		);
	};

	if (loading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<div className="loader"></div>
			</div>
		);
	}

	return (
		<>
			<div className="flex">
				<Link to={`..`}>
					<ArrowLeft />
				</Link>
			</div>
			<div className="flex flex-col w-2/4 bg-slate-100 p-4 rounded-md gap-2">
				<TextSection title={t('Alerts')} value={''} />
				<TextSection title={t('Status')} value={data?.getRegistry?.status || ''} />
				<TextSection title={t('Email')} value={''} />
			</div>

			<div className="flex gap-5">
				<MediaCard
					translationKey="ClientProspectus"
					title={t('TaxCertificateFileTitle')}
					presignedUrl={
						'https://app-finclu-tests.s3.amazonaws.com/revamp-local-tests/some-random-id/tax_status_certificate.pdf?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=AKIAQCEPU5GXGLHM5GTX%2F20241105%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20241105T004254Z&X-Amz-Expires=3600&X-Amz-SignedHeaders=host&X-Amz-Signature=a81c6c2fc2af1eebb787a80f6b414f3a779b3181f12a1955dde14aff04214eba'
					}
					// temporary hardcoded
				/>
				<MediaCard
					translationKey="ClientProspectus"
					title={'INE'}
					presignedUrl={
						'https://app-finclu-tests.s3.amazonaws.com/revamp-local-tests/diagram.png?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=AKIAQCEPU5GXGLHM5GTX%2F20241105%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20241105T011907Z&X-Amz-Expires=3600&X-Amz-SignedHeaders=host&X-Amz-Signature=caad3f9be814f7298e46e830e0328e66a95219ea2f451c3c230ce298fc7370be'
					}
					// temporary hardcoded
				/>
			</div>
			<div className="flex flex-col gap-2 w-full p-6 bg-slate-100 overflow-y-auto max-h-[calc(70vh)] custom-scrollbar rounded">
				<div className="flex flex-row items-center">
					<div className="grow">
						<Typography weight="bold" size="xl">
							{t('KYCFraud')}
						</Typography>
					</div>
					<div className="flex flex-row gap-2 items-center">
						<Tooltip content={t('ExpandAll')}>
							<IconButton
								icon={<ChevronsDown />}
								onClick={expandAll}
								size="xsmall"
								disabled={openSections.size === sectionTitles.length}
							/>
						</Tooltip>
						<Tooltip content={t('CollapseAll')}>
							<IconButton
								icon={<ChevronsUp />}
								onClick={collapseAll}
								size="xsmall"
								disabled={openSections.size === 0}
							/>
						</Tooltip>
					</div>
				</div>
				{informationSections.map((section, index) => (
					<InfoSectionDropdown
						key={index}
						title={sectionTitles[index]}
						data={section}
						t={t}
						openSections={openSections}
						setOpenSections={setOpenSections}
					/>
				))}
			</div>
		</>
	);
};

export default ClientProspectus;
